import React from 'react';
import { trendData } from '../../data/mockData';
import StatusBadge from '../common/StatusBadge';
import ScoreIndicator from '../common/ScoreIndicator';
import ChevronIcon from '../common/ChevronIcon';

const Dashboard = ({ state }) => {
  const {
    activeDomain, setActiveDomain,
    quickMaiaQuery, setQuickMaiaQuery,
    setMaiaQuery, setShowQuickMaia,
    packages,
    generatedBriefs,
    researchBriefs,
    decisionHistory,
    sectionsCollapsed,
    toggleSection,
    selectTrend,
    getStats,
    setSelectedPackage, setActiveView,
  } = state;

  const stats = getStats();

  // Calculate domain-specific stats
  const contentBriefsSent = generatedBriefs.filter(b => b.contentBrief?.status === 'sent').length;
  const audienceBriefsSent = generatedBriefs.filter(b => b.audienceBrief).length;
  const packagesInProgress = packages.filter(p => p.status === 'in_progress').length;

  // Trend Intake Component
  const TrendIntakeSection = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: '600', color: '#f3f4f6' }}>Incoming Trends</h2>
        <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: 13 }}>
          {trendData.length} opportunities pending review from Trend Agent
        </p>
      </div>
      <div style={{ display: 'grid', gap: 16 }}>
        {trendData.map(trend => (
          <div
            key={trend.id}
            onClick={() => selectTrend(trend)}
            style={{
              backgroundColor: '#1e1e32',
              borderRadius: 12,
              padding: 24,
              cursor: 'pointer',
              border: '1px solid #2d2d44',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.borderColor = '#6366f1';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.borderColor = '#2d2d44';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: '600', color: '#f3f4f6' }}>{trend.signal}</h3>
                  <StatusBadge status={trend.status} />
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 13, color: '#9ca3af' }}>
                  <span style={{ backgroundColor: '#2d2d44', padding: '2px 8px', borderRadius: 4 }}>{trend.macro}</span>
                  <span>{'\u2192'}</span>
                  <span style={{ backgroundColor: '#2d2d44', padding: '2px 8px', borderRadius: 4 }}>{trend.micro}</span>
                </div>
              </div>
              <ScoreIndicator score={Math.round(trend.trendScore * 10)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginTop: 20 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Velocity</div>
                <div style={{ fontSize: 15, fontWeight: '500', color: trend.velocity === 'High' ? '#10b981' : '#f59e0b' }}>{trend.velocity}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sources</div>
                <div style={{ fontSize: 15, fontWeight: '500', color: '#f3f4f6' }}>{trend.sources}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Search Growth</div>
                <div style={{ fontSize: 15, fontWeight: '500', color: '#10b981' }}>{trend.externalSignals.searchVolume}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Mentions</div>
                <div style={{ fontSize: 15, fontWeight: '500', color: '#f3f4f6' }}>{trend.externalSignals.socialMentions}</div>
              </div>
            </div>
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #2d2d44' }}>
              <span style={{ fontSize: 13, color: '#6366f1', fontWeight: '500' }}>Click to execute deep dive analysis {'\u2192'}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Content Domain View
  const ContentDomainView = () => (
    <div>
      {/* Packages in Progress */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: '600', color: '#f3f4f6' }}>Packages in Progress</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>
              Bundled content briefs for coordinated delivery to CSA
            </p>
          </div>
          <span style={{ padding: '4px 12px', backgroundColor: '#8b5cf620', borderRadius: 12, fontSize: 12, fontWeight: '600', color: '#a78bfa' }}>
            {packagesInProgress}
          </span>
        </div>
        {packagesInProgress === 0 ? (
          <div style={{ padding: 24, backgroundColor: '#252540', borderRadius: 12, border: '1px dashed #374151', textAlign: 'center' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>No packages in progress. Add approved briefs to a package.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {packages.filter(p => p.status === 'in_progress').map(pkg => (
              <div
                key={pkg.id}
                onClick={() => { setSelectedPackage(pkg); setActiveView('package'); }}
                style={{
                  backgroundColor: '#252540',
                  borderRadius: 12,
                  padding: 20,
                  border: '1px solid #374151',
                  borderLeft: '4px solid #8b5cf6',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.borderLeftColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: '600', color: '#f3f4f6' }}>{pkg.name}</h4>
                    <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>{pkg.macroTrend}</p>
                  </div>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#8b5cf620', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa', fontWeight: '700', fontSize: 13 }}>
                    {pkg.briefs.length}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  {pkg.categories.slice(0, 2).map((cat, idx) => (
                    <span key={idx} style={{ padding: '2px 6px', backgroundColor: '#3b82f620', borderRadius: 3, fontSize: 9, color: '#93c5fd' }}>{cat}</span>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: '500' }}>View Package {'\u2192'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Individual Briefs Sent to CSA */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: '600', color: '#f3f4f6' }}>Individual Briefs</h3>
            <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>Content briefs sent directly to Content Scaling Agent</p>
          </div>
          <span style={{ padding: '4px 12px', backgroundColor: '#10b98120', borderRadius: 12, fontSize: 12, fontWeight: '600', color: '#10b981' }}>
            {contentBriefsSent}
          </span>
        </div>
        {contentBriefsSent === 0 ? (
          <div style={{ padding: 24, backgroundColor: '#252540', borderRadius: 12, border: '1px dashed #374151', textAlign: 'center' }}>
            <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>No briefs sent directly to CSA yet.</p>
          </div>
        ) : (
          <div style={{ backgroundColor: '#252540', borderRadius: 12, border: '1px solid #374151', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 120px', gap: 16, padding: '10px 16px', backgroundColor: '#1e1e32', fontSize: 10, fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
              <div>Trend</div>
              <div>Category</div>
              <div>Status</div>
              <div>Sent</div>
            </div>
            {generatedBriefs.filter(b => b.contentBrief?.status === 'sent').map(brief => (
              <div key={brief.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 120px', gap: 16, padding: '12px 16px', borderTop: '1px solid #374151', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: '500', color: '#f3f4f6' }}>{brief.trend}</div>
                <div style={{ fontSize: 12, color: '#9ca3af' }}>{brief.macro}</div>
                <div><span style={{ padding: '3px 8px', backgroundColor: '#10b98120', borderRadius: 4, fontSize: 10, color: '#10b981' }}>Sent to CSA</span></div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{brief.contentBrief?.sentAt}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Audience Domain View
  const AudienceDomainView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: '600', color: '#f3f4f6' }}>Audience Briefs</h3>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>
            Distribution & engagement strategies sent to Growth teams
          </p>
        </div>
        <span style={{ padding: '4px 12px', backgroundColor: '#f59e0b20', borderRadius: 12, fontSize: 12, fontWeight: '600', color: '#f59e0b' }}>
          {audienceBriefsSent}
        </span>
      </div>
      {audienceBriefsSent === 0 ? (
        <div style={{ padding: 32, backgroundColor: '#252540', borderRadius: 12, border: '1px dashed #374151', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{'\uD83D\uDC65'}</div>
          <p style={{ margin: '0 0 8px', color: '#f3f4f6', fontSize: 14, fontWeight: '500' }}>No audience briefs generated yet</p>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Generate audience briefs from the Content Brief view to populate this section.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: '#252540', borderRadius: 12, border: '1px solid #374151', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 120px', gap: 16, padding: '10px 16px', backgroundColor: '#1e1e32', fontSize: 10, fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>
            <div>Trend</div>
            <div>Category</div>
            <div>Status</div>
            <div>Sent</div>
          </div>
          {generatedBriefs.filter(b => b.audienceBrief).map(brief => (
            <div key={brief.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 120px', gap: 16, padding: '12px 16px', borderTop: '1px solid #374151', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: '500', color: '#f3f4f6' }}>{brief.trend}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{brief.macro}</div>
              <div><span style={{ padding: '3px 8px', backgroundColor: '#f59e0b20', borderRadius: 4, fontSize: 10, color: '#f59e0b' }}>Sent to Queue</span></div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{brief.audienceBrief?.sentAt}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Research Domain View
  const ResearchDomainView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: '600', color: '#f3f4f6' }}>Research Briefs</h3>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 12 }}>
            MAIA deep research sessions and insights
          </p>
        </div>
        <button
          onClick={() => setShowQuickMaia(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 16px',
            backgroundColor: '#f59e0b',
            border: 'none',
            borderRadius: 8,
            color: '#0f0f1a',
            fontSize: 12,
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <span>+</span> New Research
        </button>
      </div>
      {researchBriefs.length === 0 ? (
        <div style={{ padding: 32, backgroundColor: '#252540', borderRadius: 12, border: '1px dashed #374151', textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>{'\uD83D\uDD2C'}</div>
          <p style={{ margin: '0 0 8px', color: '#f3f4f6', fontSize: 14, fontWeight: '500' }}>No research briefs yet</p>
          <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>Use the MAIA Research button to conduct deep research on trends and topics.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {researchBriefs.map(brief => (
            <div
              key={brief.id}
              style={{
                backgroundColor: '#252540',
                borderRadius: 12,
                padding: 20,
                border: '1px solid #374151',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseOut={(e) => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>{'\uD83D\uDD2C'}</span>
                    <h4 style={{ margin: 0, fontSize: 14, fontWeight: '600', color: '#f3f4f6' }}>{brief.title}</h4>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: '#9ca3af', lineHeight: 1.5 }}>{brief.query}</p>
                </div>
                {brief.trendContext && (
                  <span style={{ padding: '4px 10px', backgroundColor: '#6366f120', borderRadius: 6, fontSize: 10, color: '#a5b4fc', whiteSpace: 'nowrap' }}>
                    {brief.trendContext}
                  </span>
                )}
              </div>
              <div style={{ padding: 12, backgroundColor: '#1e1e32', borderRadius: 8, marginBottom: 12 }}>
                <p style={{ margin: 0, fontSize: 12, color: '#d1d5db', lineHeight: 1.6 }}>{brief.summary}</p>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {brief.agents.map((agent, idx) => (
                    <span key={idx} style={{ padding: '3px 8px', backgroundColor: '#374151', borderRadius: 4, fontSize: 10, color: '#9ca3af' }}>
                      {agent}
                    </span>
                  ))}
                </div>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{brief.createdAt}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // Business Development Domain View (Placeholder)
  const BusinessDevDomainView = () => (
    <div style={{ padding: 48, textAlign: 'center' }}>
      <div style={{ fontSize: 56, marginBottom: 16 }}>{'\uD83E\uDD1D'}</div>
      <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: '600', color: '#f3f4f6' }}>Business Development</h3>
      <p style={{ margin: '0 0 24px', color: '#9ca3af', fontSize: 14, maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
        Curated product recommendations tied to trend themes for partnership and monetization opportunities.
      </p>
      <div style={{ padding: 24, backgroundColor: '#252540', borderRadius: 12, border: '1px dashed #374151', maxWidth: 500, margin: '0 auto' }}>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>
          Coming soon: Product recommendation engine, brand partnership briefs, and affiliate opportunity matching.
        </p>
      </div>
    </div>
  );

  // Decision History Component (collapsible)
  const DecisionHistorySection = () => (
    <div style={{ marginTop: 32 }}>
      <div
        style={{ cursor: 'pointer', userSelect: 'none', marginBottom: 16 }}
        onClick={() => toggleSection('decisionHistory')}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChevronIcon collapsed={sectionsCollapsed.decisionHistory} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: '600', color: '#9ca3af' }}>Decision History</h3>
          <span style={{ padding: '2px 8px', backgroundColor: '#374151', borderRadius: 8, fontSize: 11, color: '#6b7280' }}>
            {decisionHistory.length}
          </span>
        </div>
      </div>
      {!sectionsCollapsed.decisionHistory && decisionHistory.length > 0 && (
        <div style={{ backgroundColor: '#1e1e32', borderRadius: 8, border: '1px solid #2d2d44', overflow: 'hidden', fontSize: 12 }}>
          {decisionHistory.slice(0, 5).map(decision => (
            <div key={decision.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid #2d2d44' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: decision.decision === 'approved' ? '#10b981' : '#ef4444' }} />
                <span style={{ color: '#f3f4f6' }}>{decision.trend}</span>
              </div>
              <span style={{ color: '#6b7280', fontSize: 11 }}>{decision.decidedAt}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Command Center Header with MAIA Search */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: '700', color: '#f3f4f6' }}>Command Center</h1>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>Trend strategy operations dashboard</p>
        </div>
        {/* MAIA Quick Search Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 16px',
          backgroundColor: '#1e1e32',
          borderRadius: 12,
          border: '1px solid #2d2d44',
          width: 400
        }}>
          <span style={{ fontSize: 18 }}>{'\uD83D\uDD2C'}</span>
          <input
            type="text"
            placeholder="Quick MAIA research... (press Enter)"
            value={quickMaiaQuery}
            onChange={(e) => setQuickMaiaQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && quickMaiaQuery.trim()) {
                setMaiaQuery(quickMaiaQuery);
                setShowQuickMaia(true);
              }
            }}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              color: '#f3f4f6',
              fontSize: 13,
              outline: 'none'
            }}
          />
          <button
            onClick={() => {
              if (quickMaiaQuery.trim()) {
                setMaiaQuery(quickMaiaQuery);
                setShowQuickMaia(true);
              }
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f59e0b',
              border: 'none',
              borderRadius: 6,
              color: '#0f0f1a',
              fontSize: 11,
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Research
          </button>
        </div>
      </div>

      {/* Navigational Stats Bar - Order: Trends, Audience, Content, Business Dev, Research */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 12,
        marginBottom: 24
      }}>
        {/* 1. Trends */}
        <div
          onClick={() => setActiveDomain('intake')}
          style={{
            padding: 14,
            backgroundColor: activeDomain === 'intake' ? '#f59e0b20' : '#1e1e32',
            borderRadius: 12,
            border: activeDomain === 'intake' ? '2px solid #f59e0b' : '1px solid #2d2d44',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: '700', color: '#f59e0b', marginBottom: 4 }}>{stats.pendingReview}</div>
          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Trends</div>
        </div>
        {/* 2. Audience */}
        <div
          onClick={() => setActiveDomain('audience')}
          style={{
            padding: 14,
            backgroundColor: activeDomain === 'audience' ? '#3b82f620' : '#1e1e32',
            borderRadius: 12,
            border: activeDomain === 'audience' ? '2px solid #3b82f6' : '1px solid #2d2d44',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: '700', color: '#3b82f6', marginBottom: 4 }}>{audienceBriefsSent}</div>
          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Audience</div>
        </div>
        {/* 3. Content */}
        <div
          onClick={() => setActiveDomain('content')}
          style={{
            padding: 14,
            backgroundColor: activeDomain === 'content' ? '#10b98120' : '#1e1e32',
            borderRadius: 12,
            border: activeDomain === 'content' ? '2px solid #10b981' : '1px solid #2d2d44',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: '700', color: '#10b981', marginBottom: 4 }}>{contentBriefsSent + packagesInProgress}</div>
          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Content</div>
        </div>
        {/* 4. Business Dev */}
        <div
          onClick={() => setActiveDomain('businessDev')}
          style={{
            padding: 14,
            backgroundColor: activeDomain === 'businessDev' ? '#8b5cf620' : '#1e1e32',
            borderRadius: 12,
            border: activeDomain === 'businessDev' ? '2px solid #8b5cf6' : '1px solid #2d2d44',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: '700', color: '#8b5cf6', marginBottom: 4 }}>{'\u2014'}</div>
          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Business Dev</div>
        </div>
        {/* 5. Research */}
        <div
          onClick={() => setActiveDomain('research')}
          style={{
            padding: 14,
            backgroundColor: activeDomain === 'research' ? '#f59e0b20' : '#1e1e32',
            borderRadius: 12,
            border: activeDomain === 'research' ? '2px solid #f59e0b' : '1px solid #2d2d44',
            cursor: 'pointer',
            transition: 'all 0.2s',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: '700', color: '#f59e0b', marginBottom: 4 }}>{researchBriefs.length}</div>
          <div style={{ fontSize: 10, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Research</div>
        </div>
      </div>

      {/* Domain Content Area */}
      <div style={{
        backgroundColor: '#1e1e32',
        borderRadius: 16,
        padding: 24,
        border: '1px solid #2d2d44',
        minHeight: 400
      }}>
        {activeDomain === 'intake' && <TrendIntakeSection />}
        {activeDomain === 'research' && <ResearchDomainView />}
        {activeDomain === 'content' && <ContentDomainView />}
        {activeDomain === 'audience' && <AudienceDomainView />}
        {activeDomain === 'businessDev' && <BusinessDevDomainView />}
      </div>

      {/* Decision History (always visible at bottom, collapsible) */}
      <DecisionHistorySection />
    </div>
  );
};

export default Dashboard;
